import React from "react";
import { styled } from "@mui/material/styles";
import "./interactiveImage.css";
import Button from "@mui/material/Button";
import Grow from "@mui/material/Grow";
import { Typography } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useDispatch, useSelector } from "react-redux";
import { useViewport, randomNumber } from "../../helpers";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import EventIcon from "@mui/icons-material/Event";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";
import CreateIcon from "@mui/icons-material/Create";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import Badge from "@mui/material/Badge";
import AddIcon from "@mui/icons-material/Add";
import { CopyToClipboard } from "react-copy-to-clipboard";
import LinkIcon from "@mui/icons-material/Link";
import Skeleton from "@mui/material/Skeleton";
import TheatersIcon from "@mui/icons-material/Theaters";
import ImageIcon from "@mui/icons-material/Image";
import LanguageIcon from "@mui/icons-material/Language";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import TimerIcon from "@mui/icons-material/Timer";
import clsx from "clsx";
import { useNavigate } from "react-router-dom/dist";
import {
  openBackdrop,
  openCreateDesign,
  openCreateDesignZones,
} from "../../reducers/myDesigns/myDesignsSlice";
import { sendNotification } from "../../reducers/myDesigns/myDesignsThunks";
import { ThreeDRotation, ViewInAr } from "@mui/icons-material";

const PREFIX = "interactiveImage";

const classes = {
  dotsContainer: `${PREFIX}-dotsContainer`,
  dots: `${PREFIX}-dots`,
  backdrop: `${PREFIX}-backdrop`,
  zoneMenuActionsPopper: `${PREFIX}-zoneMenuActionsPopper`,
  MenuItemIcon: `${PREFIX}-MenuItemIcon`,
  designTitle: `${PREFIX}-designTitle`,
  textCenter: `${PREFIX}-textCenter`,
  leftMargin: `${PREFIX}-leftMargin`,
  paddingMenuList: `${PREFIX}-paddingMenuList`,
  designTitleAddCard: `${PREFIX}-designTitleAddCard`,
  copyLinkButton: `${PREFIX}-copyLinkButton`,
  noContentIcon: `${PREFIX}-noContentIcon`,
  contentIcon: `${PREFIX}-contentIcon`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.dotsContainer}`]: {
    position: "absolute",
    bottom: 0,
    right: "-15px",
    width: "40px",
    height: "38px",
    cursor: "pointer",
    zIndex: 998,
    "&:hover": {
      backgroundColor: "transparent",
    },
  },

  [`& .${classes.dots}`]: {
    "Webkit-transition": "all .4s ease",
    MozTransition: "all .4s ease",
    OTransition: "all .4s ease",
    transition: "all .4s ease",
    color: "#fff",
    "&:hover": {
      "Webkit-transition": "all .4s ease",
      MozTransition: "all .4s ease",
      OTransition: "all .4s ease",
      transition: "all .4s ease",
      color: theme.palette.primary.main,
    },
  },

  [`& .${classes.backdrop}`]: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },

  [`& .${classes.zoneMenuActionsPopper}`]: {
    zIndex: 999,
    "& > div": {
      backgroundColor: "#424242",
    },
  },

  [`& .${classes.MenuItemIcon}`]: {
    color: theme.palette.primary.main,
  },

  [`& .${classes.designTitle}`]: {
    maxWidth: "80%",
  },

  [`& .${classes.textCenter}`]: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textAlign: "center",
  },

  [`& .${classes.leftMargin}`]: {
    marginLeft: "12px",
  },

  [`& .${classes.paddingMenuList}`]: {
    padding: "10px",
  },

  [`& .${classes.designTitleAddCard}`]: {
    position: "relative",
    top: "1.5rem",
    width: "50%",
    margin: "0 auto",
    textAlign: "center",
  },

  [`& .${classes.copyLinkButton}`]: {
    position: "absolute",
    right: 0,
    zIndex: 998,
  },

  [`& .${classes.noContentIcon}`]: {
    color: "red !important",
  },

  [`& .${classes.contentIcon}`]: {
    position: "absolute",
    left: "0.5rem",
    top: "0.5rem",
    zIndex: 998,
    width: "28px",
    height: "28px",
    color: theme.palette.primary.main,
    backgroundColor: "white",
    borderRadius: "50%",
    padding: "0.06rem",
  },
}));

export default function InteractiveImage(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isHovered, setHover] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const history = useNavigate();
  const [url, setUrl] = React.useState(null);
  const [error, setError] = React.useState(false);
  const [clipboard, setClipboard] = React.useState({
    value: `${process.env.REACT_APP_API_URL}/r/${props.myDesign.uniqueKey}`,
    copied: false,
  });
  const tmpInfos = { ...props.myDesign, title: props.frameName };
  const currentDesignImageUrl = useSelector(
    (state) => state.myDesigns.currentDesign?.imageUrl
  );
  const [openDesktopMenu, setOpenDesktopMenu] = React.useState(false);
  const anchorRef = React.useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const { width } = useViewport();

  const crmToggle = useSelector((state) => state.myDesigns?.crmEnabled);

  React.useEffect(() => {
    const myUrl = props.myDesign.imageBorderlessUrl
      ? props.myDesign.imageBorderlessUrl
      : props.myDesign.imageUrl
      ? props.myDesign.imageUrl
      : currentDesignImageUrl;
    setUrl(myUrl);
    if (props.myDesign) {
      setClipboard({
        value: `${process.env.REACT_APP_API_URL}/r/${props.myDesign.uniqueKey}`,
        copied: false,
      });
    }
  }, [props.target, props.myDesign]);

  const handleToggle = () => {
    setOpenDesktopMenu(!openDesktopMenu);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpenDesktopMenu(false);
  };

  function handleListKeyDown(event) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpenDesktopMenu(false);
    }
  }

  function imageLoaded() {
    setLoading(false);
  }

  function handleImageError() {
    imageLoaded();
    setLoading(false);
    setError(true);
  }

  const mySendNotification = async () => {
    try {
      const thunk = await dispatch(
        sendNotification({
          frameId: props.myDesign.frameId,
          frameName: props.myDesign.title,
          label: props.myDesign.target,
          zoneName: props.myDesign.label,
        })
      );
      if (thunk.meta.requestStatus === "rejected")
        throw new Error("recovery error");
      enqueueSnackbar(t("toasts.notificationSent"), { variant: "success" });
    } catch (err) {
      enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
    }
  };

  if (props.addCard) {
    return (
      <Root>
        <div
          className="my-design-add-item"
          onClick={() =>
            !props.zonesMode
              ? dispatch(openCreateDesign())
              : dispatch(openCreateDesignZones())
          }
        >
          <div className={classes.designTitleAddCard}>
            <AddIcon style={{ width: "50px", height: "50px" }} />
            <div>{props.myDesign.name}</div>
          </div>
        </div>
      </Root>
    );
  } else {
    const PreviewItemContent = () => {
      if (props.myDesign.planifications === 1)
        return (
          <Root>
            <TimerIcon className={classes.contentIcon} />
          </Root>
        );
      else if (props.myDesign.playlist === 1) {
        if (props.myDesign.type) {
          return (
            <Root>
              <PlayCircleOutlineIcon className={classes.contentIcon} />
            </Root>
          );
        } else
          return (
            <Root>
              <BlockIcon
                className={clsx(classes.contentIcon, classes.noContentIcon)}
              />
            </Root>
          );
      } else if (props.myDesign.playlist === 0) {
        switch (props.myDesign.type) {
          case "video":
            return (
              <Root>
                <TheatersIcon className={classes.contentIcon} />
              </Root>
            );
          case "model":
            return (
              <Root>
                <ThreeDRotation className={classes.contentIcon} />
              </Root>
            );
          case "link":
            if (props.myDesign.targetUrl && props.myDesign.targetUrl.length > 0)
              return (
                <Root>
                  <LanguageIcon className={classes.contentIcon} />
                </Root>
              );
            else
              return (
                <Root>
                  <BlockIcon
                    className={clsx(classes.contentIcon, classes.noContentIcon)}
                  />
                </Root>
              );
          case "image":
            return (
              <Root>
                <ImageIcon className={classes.contentIcon} />
              </Root>
            );
          default:
            return (
              <Root>
                <BlockIcon
                  className={clsx(classes.contentIcon, classes.noContentIcon)}
                />
              </Root>
            );
        }
      } else {
        return null;
      }
    };
    return (
      <Root>
        <div
          className="my-design-item"
          onMouseOver={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onClick={(e) => {
            e.stopPropagation();
            if (!props.zonesMode) {
              if (crmToggle) {
                history(`/mydesign/${props.myDesign.myDesignId}`, {
                  state: {
                    from: "search",
                    index: 0,
                    key: "targets",
                    item: { id: props.myDesign.targetId },
                  },
                });
              } else history(`/mydesign/${props.myDesign.id}`);
            } else {
              if (
                props.isAdmin ||
                props.myDesign.saveAccess === 1 ||
                props.myDesign.uploadAccess === 1 ||
                props.myDesign.scheduleAccess === 1 ||
                props.myDesign.previewAccess === 1 ||
                props.myDesign.notifyAccess === 1
              )
                history(
                  `/mydesign/${props.myDesign.frameId}/zone/${props.myDesign.id}`
                );
            }
          }}
        >
          {props.zonesMode && (
            <React.Fragment>
              <Popper
                open={openDesktopMenu}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                className={classes.zoneMenuActionsPopper}
                placement="bottom-end"
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin:
                        placement === "bottom" ? "center top" : "center bottom",
                    }}
                  >
                    <Paper>
                      <ClickAwayListener onClickAway={handleClose}>
                        <MenuList
                          id="menu-list-grow"
                          onKeyDown={handleListKeyDown}
                        >
                          {(props.myDesign.saveAccess === 1 ||
                            props.myDesign.uploadAccess === 1 ||
                            props.isAdmin) && (
                            <MenuItem
                              className={classes.paddingMenuList}
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDesktopMenu(false);
                                dispatch(
                                  openBackdrop({
                                    from: "modify",
                                    infos: tmpInfos,
                                  })
                                );
                              }}
                            >
                              <CreateIcon className={classes.MenuItemIcon} />
                              <Typography
                                variant="body2"
                                className={classes.leftMargin}
                              >
                                {t("myDesign.zones.actionsMenu.modify")}
                              </Typography>
                            </MenuItem>
                          )}
                          {(props.myDesign.scheduleAccess === 1 ||
                            props.isAdmin) && (
                            <MenuItem
                              className={classes.paddingMenuList}
                              onClick={(e) => {
                                e.stopPropagation();
                                history(
                                  `/mydesign/${props.myDesign.frameId}/zone/${props.myDesign.id}`,
                                  {
                                    state: {
                                      myDesign: props.myDesign,
                                      index: 1,
                                    },
                                  }
                                );
                              }}
                            >
                              <Badge
                                badgeContent={props.myDesign.planifications}
                                color="secondary"
                                className={classes.badge}
                                overlap="rectangular"
                              >
                                <EventIcon className={classes.MenuItemIcon} />
                              </Badge>
                              <Typography
                                variant="body2"
                                className={classes.leftMargin}
                              >
                                {t("myDesign.zones.actionsMenu.schedule")}
                              </Typography>
                            </MenuItem>
                          )}
                          {(props.myDesign.previewAccess === 1 ||
                            props.isAdmin) && (
                            <MenuItem
                              className={classes.paddingMenuList}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(props.myDesign.targetUrl, "_blank");
                              }}
                            >
                              <FindInPageIcon
                                className={classes.MenuItemIcon}
                              />
                              <Typography
                                variant="body2"
                                className={classes.leftMargin}
                              >
                                {t("myDesign.zones.actionsMenu.preview")}
                              </Typography>
                            </MenuItem>
                          )}
                          {(props.myDesign.notifyAccess === 1 ||
                            props.isAdmin) && (
                            <MenuItem
                              className={classes.paddingMenuList}
                              onClick={(e) => {
                                e.stopPropagation();
                                mySendNotification();
                              }}
                            >
                              <SettingsInputAntennaIcon
                                className={classes.MenuItemIcon}
                              />
                              <Typography
                                variant="body2"
                                className={classes.leftMargin}
                              >
                                {t("myDesign.zones.actionsMenu.notify")}
                              </Typography>
                            </MenuItem>
                          )}
                          <MenuItem
                            className={classes.paddingMenuList}
                            onClick={(e) => {
                              e.stopPropagation();
                              history(
                                `/mydesign/${props.myDesign.frameId}/zone/${props.myDesign.id}`,
                                {
                                  state: {
                                    myDesign: props.myDesign,
                                  },
                                }
                              );
                            }}
                          >
                            <i
                              className="fas fa-cog"
                              style={{ color: "#D3AF5F", margin: "0 0.25rem" }}
                            ></i>
                            <Typography
                              variant="body2"
                              className={classes.leftMargin}
                            >
                              {t("myDesign.zones.actionsMenu.options")}
                            </Typography>
                          </MenuItem>
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </React.Fragment>
          )}
          <div style={{ position: "relative" }}>
            {props.isAdmin && props.zonesMode && (
              <CopyToClipboard
                onCopy={() => setClipboard({ ...clipboard, copied: true })}
                text={clipboard.value}
              >
                <IconButton
                  className={classes.copyLinkButton}
                  aria-label="copy link"
                  color="primary"
                  onClick={(e) => {
                    enqueueSnackbar(t("toasts.copiedInClipboard"), {
                      variant: "info",
                    });
                    e.stopPropagation();
                  }}
                  size="large"
                >
                  <LinkIcon />
                </IconButton>
              </CopyToClipboard>
            )}
            {props.zonesMode && <PreviewItemContent />}
            <img
              src={!error ? url : props.parentUrl}
              alt={props.myDesign.label}
              className="my-design-image"
              style={{
                filter: isHovered && "blur(3px)",
                display: loading && "none",
              }}
              onError={
                loading &&
                !props.myDesign.imageUrl &&
                !props.myDesign.imageBorderlessUrl
                  ? handleImageError()
                  : undefined
              }
              onLoad={imageLoaded}
            />
          </div>
          {!loading && (
            <div className="my-design-empty-item">
              <div
                className={
                  props.zonesMode ? classes.designTitle : classes.textCenter
                }
              >
                <span>{props.myDesign.label}</span>
              </div>
              {props.zonesMode && (
                <Button
                  ref={anchorRef}
                  aria-controls={openDesktopMenu ? "menu-list-grow" : undefined}
                  aria-haspopup="true"
                  className={classes.dotsContainer}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (width > 425) {
                      handleToggle();
                    } else {
                      dispatch(openBackdrop({ from: "zone", infos: tmpInfos }));
                    }
                  }}
                >
                  <MoreHorizIcon className={classes.dots} />
                </Button>
              )}
            </div>
          )}
          {loading && (
            <Skeleton
              variant="rectangular"
              style={{
                height: randomNumber(50, 300),
              }}
            />
          )}
          {isHovered && !props.zonesMode && (
            <Grow in={isHovered} timeout={750}>
              <Button
                variant="contained"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  if (crmToggle) {
                    history(`/mydesign/${props.myDesign.myDesignId}`, {
                      state: {
                        from: "search",
                        index: 0,
                        key: "targets",
                        item: { id: props.myDesign.targetId },
                      },
                    });
                  } else history(`/mydesign/${props.myDesign.id}`);
                }}
                style={{
                  position: "absolute",
                  top: "50%",
                  right: "50%",
                  transform: "translate(50%, -50%)",
                  zIndex: "999",
                }}
              >
                <Typography variant="body1">
                  {t("homePage.myDesign.interactiveDesign.visualize")}
                </Typography>
              </Button>
            </Grow>
          )}
        </div>
      </Root>
    );
  }
}
